/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module CollectionsModule
 */

import { Collection } from 'ajs/modules/data-model/factories/collection.factory';
import { ControllerBannerAlertItem, ControllerBannerAlertSeverity } from 'items/controller-banner-alert.item.factory';

/**
 * Interface for banner alert item used in app-level alerts.
 */
export interface IBannerAlertMessage {
    service?: string;
    name?: string;
    description?: string;
    severity?: ControllerBannerAlertSeverity;
}

export type TControllerBannerAlertCollection = typeof ControllerBannerAlertCollection;

const DEFAULT_PAGE = 1;

/**
 * Map a ControllerBannerAlertItem to IBannerAlertMessage format.
 * @param item - The banner alert item to map.
 * @return The mapped banner alert message.
 */
function mapItemToAlertMessage(item: ControllerBannerAlertItem): IBannerAlertMessage {
    return {
        service: item.service,
        name: item.alertName,
        description: item.description,
        severity: item.severity,
    };
}

/**
 * @description Collection of Banner Alerts from /api/inventory/banner endpoint.
 * @author Shubham Bindal
 */
export class ControllerBannerAlertCollection extends Collection {
    public static readonly ngDependencies = [ControllerBannerAlertItem];

    constructor(args = {}) {
        const extendedArgs = {
            objectName: 'inventory/banner',
            allDataSources: {
                list: {
                    source: 'ControllerFaultCollDataSource',
                    transformer: 'BannerAlertDataTransformer',
                    transport: 'ListDataTransport',
                    fields: ['config'],
                },
            },
            params: {
                page: DEFAULT_PAGE,
            },
            ...args,
        };

        super(extendedArgs);

        this.itemClass_ = this.getNgDependency(ControllerBannerAlertItem);
    }

    /**
     * Return a list of all banner alert messages grouped by severity.
     * @return Object containing arrays of alerts by severity.
     */
    public get alertsBySeverity(): Record<ControllerBannerAlertSeverity, IBannerAlertMessage[]> {
        const result: Record<ControllerBannerAlertSeverity, IBannerAlertMessage[]> = {
            [ControllerBannerAlertSeverity.ERROR]: [],
            [ControllerBannerAlertSeverity.WARN]: [],
            [ControllerBannerAlertSeverity.INFO]: [],
        };

        this.bannerAlertItems.forEach((item: ControllerBannerAlertItem) => {
            const alertMessage = mapItemToAlertMessage(item);

            if (result[item.severity]) {
                result[item.severity].push(alertMessage);
            }
        });

        return result;
    }

    /**
     * Return a flat list of all banner alert messages.
     * @return Array of banner alert messages.
     */
    public get allAlerts(): IBannerAlertMessage[] {
        return this.bannerAlertItems.map(item => mapItemToAlertMessage(item));
    }

    /**
     * Check if there are any alerts.
     */
    public get hasAlerts(): boolean {
        return this.bannerAlertItems.length > 0;
    }

    /**
     * Check if there are any error-level alerts.
     */
    public get hasErrorAlerts(): boolean {
        return this.bannerAlertItems.some(
            item => item.severity === ControllerBannerAlertSeverity.ERROR,
        );
    }

    /**
     * Check if there are any warning-level alerts.
     */
    public get hasWarningAlerts(): boolean {
        return this.bannerAlertItems.some(
            item => item.severity === ControllerBannerAlertSeverity.WARN,
        );
    }

    /**
     * Check if there are any info-level alerts.
     */
    public get hasInfoAlerts(): boolean {
        return this.bannerAlertItems.some(
            item => item.severity === ControllerBannerAlertSeverity.INFO,
        );
    }

    /**
     * Get banner alert items with proper type casting.
     * Reusable getter to avoid repeated type casting across methods.
     */
    private get bannerAlertItems(): ControllerBannerAlertItem[] {
        return (this.itemList || []) as ControllerBannerAlertItem[];
    }

    /**
     * Get alerts filtered by severity.
     * @param severity - Severity level to filter by.
     * @return Array of banner alert messages with the specified severity.
     */
    public getAlertsBySeverity(severity: ControllerBannerAlertSeverity): IBannerAlertMessage[] {
        return this.alertsBySeverity[severity] ?? [];
    }
}
